// ============================================================
//  src/pages/auth/VerifyEmailPage.js
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { verifyEmail as apiVerify, resendVerification } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { getError } from '../../utils/helpers';
import './auth.css';

export default function VerifyEmailPage() {
  const nav   = useNavigate();
  const loc   = useLocation();
  const { setUser } = useAuth();
  const passedEmail = loc.state?.email || '';

  const [email, setEmail]     = useState(passedEmail);
  const [digits, setDigits]   = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const onDigit = (idx, val) => {
    const v = val.replace(/\D/g, '').slice(0, 1);
    setDigits((p) => {
      const n = [...p];
      n[idx] = v;
      return n;
    });
    if (v && idx < 5) inputs.current[idx + 1]?.focus();
  };

  const onKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    inputs.current[Math.min(text.length, 5)]?.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    const otp = digits.join('');
    if (otp.length !== 6) { setError('Enter the 6-digit code.'); return; }
    if (!email)           { setError('Email is required.'); return; }

    setLoading(true); setError('');
    try {
      const { data } = await apiVerify({ action: 'verify', email, otp });
      if (!data?.success) { setError(data?.message || 'Verification failed.'); return; }
      // Auto-login from the freshly issued token
      const tok  = data?.data?.token;
      const user = data?.data?.user;
      if (tok) localStorage.setItem('authToken', tok);
      if (user) {
        localStorage.setItem('authUser', JSON.stringify(user));
        setUser(user);
      }
      toast.success('Email verified.');
      nav('/dashboard', { replace: true });
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!email) { setError('Enter your email first.'); return; }
    try {
      const { data } = await resendVerification({ email });
      toast.success(data?.message || 'Sent.');
      setCooldown(45);
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-main">
          <div className="auth-brand">
            <span className="auth-brand-dot">C</span>
            <span>CommandKit</span>
          </div>
          <div>
            <div className="auth-headline">One last step.<br /><em>Verify your email.</em></div>
            <p className="auth-sub">
              We sent a 6-digit code to your inbox. Enter it here to activate
              your account and jump straight into the dashboard.
            </p>
            <ul className="auth-features">
              <li><i className="fas fa-check" /> Code expires in 15 minutes</li>
              <li><i className="fas fa-check" /> Check spam if you don't see it</li>
              <li><i className="fas fa-check" /> Resend anytime if needed</li>
            </ul>
          </div>
          <div className="auth-footer-badge">
            <i className="fas fa-lock" /> Encrypted in transit
          </div>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-shell">
          <div className="auth-mobile-brand">
            <span className="auth-brand-dot">C</span> CommandKit
          </div>

          <div className="auth-hdr">
            <h2>Verify your email</h2>
            <p>Enter the 6-digit code we sent to your inbox.</p>
          </div>

          {error && (
            <div className="auth-alert auth-alert-error">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          <form onSubmit={submit} noValidate>
            {!passedEmail && (
              <div className="auth-field">
                <label>Email</label>
                <div className="auth-field-wrap">
                  <i className="fas fa-envelope fi" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>
            )}

            <label className="form-label">Verification code</label>
            <div className="otp-grid" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputs.current[i] = el)}
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => onDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                />
              ))}
            </div>

            <button type="submit" className="btn-auth" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Verifying…</>
                : <><i className="fas fa-check" /> Verify email</>
              }
            </button>

            <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13.5, color: 'var(--text-muted)' }}>
              Didn't get the code?{' '}
              {cooldown > 0
                ? <span>Resend in {cooldown}s</span>
                : <button type="button" onClick={resend} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, cursor: 'pointer' }}>Resend</button>
              }
            </div>
          </form>

          <div className="auth-bottom">
            Wrong account? <Link to="/login">Sign in instead</Link>
          </div>
        </div>
      </div>
    </div>
  );
}